// The reassignment list: everything a member is still named on (backend lib/ownership.ts), grouped
// by register, each row linking to where it is reassigned. Shown after a member is removed and from
// the Deactivated list. Nothing is moved for the administrator — who takes each record over is a
// decision.

import React from "react";
import { Link } from "react-router-dom";
import { theme, floatingSheet } from "../theme";
import { SheetCard } from "./Drawing";
import { fmtDateOnly } from "../lib/dateOnly";

export type OwnershipKind =
  | "asset" | "risk" | "supplier" | "corrective_action" | "business_impact" | "ai_system"
  | "site_responsibility" | "policy_approval" | "calendar_task"
  | "ms_context" | "ms_objective" | "ms_competence" | "ms_obligation" | "ms_aspect" | "ms_hazard"
  | "ms_audit_programme" | "ms_internal_audit" | "ms_management_review" | "response_room_action" | "action_step" | "evidence_renewal"
  | "policy_exception" | "policy_exception_approval"
  | "change_request" | "change_approval"
  | "control_owner";

export interface OwnedRecord { kind: OwnershipKind; id: string; label: string; href: string; dueAt?: string | null }

const KIND_LABELS: Record<OwnershipKind, string> = {
  asset: "Assets", risk: "Risks", supplier: "Supplier relationships", corrective_action: "Corrective actions",
  business_impact: "Business impact analyses", ai_system: "AI systems", site_responsibility: "Site responsibilities",
  policy_approval: "Policy approvals waiting on them", calendar_task: "Calendar tasks",
  ms_context: "Context and interested parties", ms_objective: "Objectives", ms_competence: "Competence requirements",
  ms_obligation: "Compliance obligations", ms_aspect: "Environmental aspects", ms_hazard: "OH&S hazards",
  ms_audit_programme: "Internal audit programmes", ms_internal_audit: "Internal audits they lead",
  ms_management_review: "Management reviews they chair", response_room_action: "Response room actions",
  action_step: "Corrective action steps given to them",
  evidence_renewal: "Evidence documents they renew",
  policy_exception: "Policy exceptions they are accountable for", policy_exception_approval: "Policy exceptions waiting on their decision",
  change_request: "Open changes they asked for", change_approval: "Changes waiting on their decision",
  control_owner: "Controls they attest",
};
const ORDER = Object.keys(KIND_LABELS) as OwnershipKind[];

export function OwnedRecordsSheet({ personName, records, onClose }: { personName: string; records: OwnedRecord[]; onClose: () => void }) {
  const groups = ORDER.map((k) => ({ kind: k, rows: records.filter((r) => r.kind === k) })).filter((g) => g.rows.length);
  return (
    <div role="dialog" aria-modal="true" aria-label={`Records to reassign from ${personName}`}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...floatingSheet, width: "min(640px, 100%)", maxHeight: "85vh", overflow: "auto" }}>
        <SheetCard label="Reassign ownership" reference={personName} style={{ border: "none" }}
          actions={<button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, fontSize: 13 }}>Close</button>}>
          {groups.length === 0 ? (
            <div style={{ fontSize: 13, color: theme.color.muted }}>{personName} is not named as owner, approver or assignee on anything open.</div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: theme.color.text, marginBottom: 12 }}>
                {personName} is still named on {records.length} record{records.length === 1 ? "" : "s"}. Give each one a new owner; the leaver
                checklist ticks "Open responsibilities reassigned" once nothing is left (ISO 27001 A.5.11, A.6.5).
              </div>
              {groups.map((g) => (
                <div key={g.kind} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--c-heading)", borderBottom: "1px solid var(--c-line)", paddingBottom: 4, marginBottom: 4 }}>
                    {KIND_LABELS[g.kind]} ({g.rows.length})
                  </div>
                  {g.rows.map((r) => (
                    <div key={`${r.kind}:${r.id}`} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "3px 0", fontSize: 13 }}>
                      <Link to={r.href} onClick={onClose} style={{ color: theme.color.accent, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</Link>
                      {r.dueAt && <span style={{ fontSize: 11, color: theme.color.subtle, whiteSpace: "nowrap" }}>{fmtDateOnly(r.dueAt)}</span>}
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </SheetCard>
      </div>
    </div>
  );
}
